import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
/* lp-tabs -- the house tab strip, the one Waypoint's record popup uses.
   It lives in lms-pages.css, which the calendar page does not import; the
   popup brings it along itself rather than drawing tabs of its own. */
import '../../../common/lms-pages.css';

/**
 * One calendar entry, opened from a chip.
 *
 * It was a SweetAlert built from an HTML string -- 300 lines of template
 * literal in `teams-*` classes, wired up afterwards by
 * document.getElementById in didOpen and a window.navigateToAttendance
 * global. That is the shape instruction_ui.json describes as the OLD one,
 * and it could not follow the page's rules because none of it was React:
 * the frame was SweetAlert's, the buttons were not .pg-btn, and the
 * handlers escaped the component tree.
 *
 * This is the frame every other popup in the app uses -- .pg-modal over
 * .pg-modal-panel--wide, head / body / foot, Close last and the only
 * filled button -- fed a plain object by handleEventClick. Nothing here
 * reads the DOM and nothing is written to window.
 */

const EventDetailModal = ({ detail, onClose, onEditEvent, onEditBatch, onViewAttendance }) => {
  // Escape closes it, the way it closes every other dialog on this page.
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === 'Escape' && !document.querySelector('.pg-modal--over')) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detail, onClose]);

  // Details or Trainers. Every entry opens on its details.
  const [tab, setTab] = useState('details');
  useEffect(() => {
    setTab('details');
  }, [detail]);

  if (!detail) return null;

  const {
    kind,
    title,
    ident = [],
    facts = [],
    trainers = [],
    audience = [],
    organizer,
    prose = [],
    links = [],
    credentials = [],
    actions = {},
  } = detail;

  return createPortal(
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cal-detail-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--wide cal-detail-panel">
        {/* The list-attendance head: what the entry is about as the title,
            in full, and the three or four facts that place it on one
            wrapping line under it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="cal-detail-title">
              {title}
            </h2>
            {ident.length > 0 && (
              <dl className="pgm-ident">
                {ident.map(([label, value]) => (
                  <div className="pgm-ident-row" key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {/* More than one trainer: their own tab, in the house strip. */}
        {trainers.length > 1 && (
          <div className="lp-tabs" role="tablist" aria-label="Entry sections">
            <button
              type="button"
              role="tab"
              id="cal-detail-tab-details"
              aria-selected={tab === 'details'}
              aria-controls="cal-detail-panel"
              className={`lp-tab${tab === 'details' ? ' is-on' : ''}`}
              onClick={() => setTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              id="cal-detail-tab-trainers"
              aria-selected={tab === 'trainers'}
              aria-controls="cal-detail-panel"
              className={`lp-tab${tab === 'trainers' ? ' is-on' : ''}`}
              onClick={() => setTab('trainers')}
            >
              Trainers
              <span className="lp-tab-n">{trainers.length}</span>
            </button>
          </div>
        )}

        {trainers.length > 1 && tab === 'trainers' ? (
          <div
            className="pg-modal-body"
            role="tabpanel"
            id="cal-detail-panel"
            aria-labelledby="cal-detail-tab-trainers"
          >
            <h5 className="pgm-section-title">
              Trainers
              <span className="pgm-section-count">{trainers.length}</span>
            </h5>
            <ol className="cal-detail-names">
              {trainers.map((name, i) => (
                <li key={`trainer-${name}-${i}`}>{name}</li>
              ))}
            </ol>
          </div>
        ) : (
        <div
          className="pg-modal-body"
          {...(trainers.length > 1
            ? { role: 'tabpanel', id: 'cal-detail-panel', 'aria-labelledby': 'cal-detail-tab-details' }
            : {})}
        >
          {/* What the entry is, as label/value pairs -- the same quiz-facts
              block the other detail popups open with. */}
          {facts.length > 0 && (
            <dl className="quiz-facts">
              {facts.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* Who it is for and who is running it. Nothing is hidden behind
              "and N more": staff open this to find out exactly which
              batches and trainers are involved, so the list runs in full
              and the dialog scrolls. */}
          {audience.map(({ label, names }) => (
            <div key={label}>
              <h5 className="pgm-section-title">
                {label}
                <span className="pgm-section-count">{names.length}</span>
              </h5>
              <ol className="cal-detail-names">
                {names.map((name, i) => (
                  <li key={`${label}-${name}-${i}`}>{name}</li>
                ))}
              </ol>
            </div>
          ))}

          {organizer && (
            <>
              <h5 className="pgm-section-title">
                {kind === 'batch' ? 'Trainer' : 'Organizer'}
              </h5>
              <div className="cal-detail-person">
                <span className="pgm-avatar">{organizer.initials}</span>
                <span className="cal-detail-person-id">
                  <b>{organizer.name}</b>
                  <small>{organizer.line}</small>
                  {organizer.email && <small>{organizer.email}</small>}
                  {organizer.phone && <small>{organizer.phone}</small>}
                </span>
              </div>
            </>
          )}

          {prose.map(([label, value]) => (
            <div key={label}>
              <h5 className="pgm-section-title">{label}</h5>
              <p className="lp-prose">{value}</p>
            </div>
          ))}

          {links.length > 0 && (
            <>
              <h5 className="pgm-section-title">Resources</h5>
              <ul className="cal-detail-links">
                {links.map(({ label, url }) => (
                  <li key={label}>
                    <span className="cal-detail-link-label">{label}</span>
                    <a
                      className="cal-detail-link-url"
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}

          {credentials.length > 0 && (
            <>
              <h5 className="pgm-section-title">Meeting credentials</h5>
              <dl className="quiz-facts">
                {credentials.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
        )}

        {/* What you can do with the entry on the left, Close alone on the
            right -- and Close is the only filled button, so nothing
            that navigates away is the default thing under the cursor. */}
        <footer className="pg-modal-foot">
          <div className="pgm-actions">
            {actions.onlineLink && (
              <a
                className="pgm-btn"
                href={actions.onlineLink}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open link
              </a>
            )}
            {actions.canViewAttendance && (
              <button type="button" className="pgm-btn" onClick={onViewAttendance}>
                View attendance
              </button>
            )}
            {actions.canEditBatch && (
              <button type="button" className="pgm-btn" onClick={onEditBatch}>
                Edit batch
              </button>
            )}
            {actions.canEditEvent && (
              <button type="button" className="pgm-btn" onClick={onEditEvent}>
                Edit event
              </button>
            )}
          </div>
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default EventDetailModal;
